import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";

export interface ReceiveIssueFieldValues {
  volumeNumber: string;
  volumePublicationYear: string;
  volumeOnly: boolean;
  issueNumberFrom: string;
  issueNumberTo: string;
  issuePublicationYear: string;
  issuePublicationDate: string;
}

interface ReceiveIssueFieldsProps {
  values: ReceiveIssueFieldValues;
  onChange: (values: ReceiveIssueFieldValues) => void;
}

export const EMPTY_RECEIVE_ISSUE_FIELDS: ReceiveIssueFieldValues = {
  volumeNumber: "",
  volumePublicationYear: "",
  volumeOnly: false,
  issueNumberFrom: "",
  issueNumberTo: "",
  issuePublicationYear: "",
  issuePublicationDate: "",
};

export function ReceiveIssueFields({ values, onChange }: ReceiveIssueFieldsProps) {
  const update = (patch: Partial<ReceiveIssueFieldValues>) => onChange({ ...values, ...patch });
  const toggleVolumeOnly = (checked: boolean) =>
    update({
      volumeOnly: checked,
      issueNumberFrom: checked ? "" : values.issueNumberFrom,
      issueNumberTo: checked ? "" : values.issueNumberTo,
      issuePublicationYear: checked ? "" : values.issuePublicationYear,
      issuePublicationDate: checked ? "" : values.issuePublicationDate,
    });

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <RequiredLabel htmlFor="journal-volume-number" required>
            Volume number
          </RequiredLabel>
          <Input
            id="journal-volume-number"
            required
            value={values.volumeNumber}
            onChange={(event) => update({ volumeNumber: event.target.value })}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="journal-volume-year">Volume year</Label>
          <Input
            id="journal-volume-year"
            value={values.volumePublicationYear}
            onChange={(event) => update({ volumePublicationYear: event.target.value })}
            className="mt-1.5"
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          checked={values.volumeOnly}
          onChange={(event) => toggleVolumeOnly(event.target.checked)}
        />
        Volume only (no issue)
      </label>
      {!values.volumeOnly ? (
        <div className="grid gap-4 border-t border-gray-200 pt-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label htmlFor="journal-issue-from">Issue number from</Label>
            <Input
              id="journal-issue-from"
              type="number"
              min={1}
              value={values.issueNumberFrom}
              onChange={(event) => update({ issueNumberFrom: event.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="journal-issue-to">Issue number to</Label>
            <Input
              id="journal-issue-to"
              type="number"
              min={1}
              value={values.issueNumberTo}
              onChange={(event) => update({ issueNumberTo: event.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="journal-issue-year">Issue year</Label>
            <Input
              id="journal-issue-year"
              value={values.issuePublicationYear}
              onChange={(event) => update({ issuePublicationYear: event.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="journal-issue-date">Issue date</Label>
            <Input
              id="journal-issue-date"
              type="date"
              value={values.issuePublicationDate}
              onChange={(event) => update({ issuePublicationDate: event.target.value })}
              className="mt-1.5"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
